/**
 * Health domain.
 *
 * Tracks medical incidents at school: injury, illness, allergic reaction,
 * medication given. The medical profile (allergies, blood type, medications,
 * immunisations) and consents (medication, off-site treatment, sports) live
 * OUTSIDE this domain — on the student doc — because they're facts, not
 * events. Only incidents flow through the domain engine.
 *
 * Schema shape (replaces the old `source`-tagged fields array):
 *   primary  — the singular headline field. One Quick Issue column. Required.
 *   followUp — secondary inputs opened in the individual-incident form after
 *              the primary pick. Conditional reveal (e.g. provider only when
 *              referredExternal=true) is decided by the form component, not
 *              the schema.
 *
 * Universal fields (recordedBy, occurredAt, state) are NOT declared here —
 * the engine writes them on every event. They were noise in the per-domain
 * config and a footgun for the old discriminator.
 *
 * `assertDomainShape` at the bottom throws synchronously on import if the
 * shape drifts — bogus configs can't run, can't build, can't test.
 */

import { assertDomainShape } from '../schemaAssert.js';
import { buildDomainCapabilities, buildDomainSurfaces } from '../centralModel.js';

export const HEALTH_DOMAIN_ID = 'health';

const PRIMARY = {
  id: 'status',
  label: 'Type',
  type: 'multiChoice',
  options: [
    { value: 'injury',            label: 'Injury',            color: 'warning' },
    { value: 'illness',           label: 'Illness',           color: 'info'    },
    { value: 'allergic-reaction', label: 'Allergic reaction', color: 'error'   },
    { value: 'medication-given',  label: 'Medication given',  color: 'success' },
    { value: 'other',             label: 'Other',             color: 'neutral' },
  ],
};

const FOLLOW_UP = [
  { id: 'location',         label: 'Location',            type: 'string',  optional: true },
  { id: 'description',      label: 'Description',         type: 'text',    optional: false },
  { id: 'actionTaken',      label: 'Action taken',        type: 'text',    optional: true },
  { id: 'referredExternal', label: 'Referred externally', type: 'boolean', optional: true },
  {
    id: 'externalProvider',
    label: 'Provider',
    type: 'string',
    optional: true,
    showWhen: { referredExternal: true },
  },
  { id: 'parentNotified',   label: 'Parent notified',     type: 'boolean', optional: true },
  {
    id: 'parentNotifiedVia',
    label: 'Notification method',
    type: 'multiChoice',
    optional: true,
    showWhen: { parentNotified: true },
    options: [
      { value: 'phone',    label: 'Phone' },
      { value: 'sms',      label: 'SMS' },
      { value: 'app',      label: 'App message' },
      { value: 'inperson', label: 'In person' },
    ],
  },
];

export const healthDomainConfig = assertDomainShape({
  name: 'health',
  label: 'Health',
  description: 'Medical incidents and wellness records',
  protected: false,
  active: true,
  sortOrder: 7,
  icon: null,
  color: 'error',
  defaultInputMethod: 'individual',
  defaultView: 'list',
  collections: {
    events: 'healthEvents',
    rules: 'healthRules',
    types: 'healthTypes',
    actions: 'healthActions',
    queue: 'healthQueue',
  },
  eventsCollection: 'healthEvents',
  rulesCollection: 'healthRules',
  capabilities: buildDomainCapabilities({
    collective: {
      enabled: true,
      aggregates: ['count', 'breakdown', 'trend', 'baseline'],
      audiences: ['staff', 'parent-summary'],
      sensitivity: 'health',
    },
  }),
  surfaces: buildDomainSurfaces({ rules: false, dashboard: true, reports: true }),
  primary: PRIMARY,
  followUp: FOLLOW_UP,
  terminology: {
    event:    { label: 'health incident',  plural: 'health incidents', description: 'A medical event at school' },
    category: { label: 'incident type',    plural: 'incident types',   description: 'Injury, illness, allergic reaction, etc.' },
    type:     { label: 'incident type',    plural: 'incident types',   description: '' },
    action:   { label: 'follow-up',        plural: 'follow-ups',       description: 'What rules trigger — parent message, nurse referral' },
    rule:     { label: 'rule',             plural: 'rules',            description: '' },
    queue:    { label: 'nurse queue',      plural: 'nurse queues',     description: '' },
  },
  contextTags: {},
  scope: { grades: [] },
});

export default healthDomainConfig;
